import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IReportEmbedConfiguration, Embed, models, service, Report } from 'powerbi-client';
import { PowerBIReportEmbedComponent } from 'powerbi-client-angular';
import { EmbeddedReportService } from '../../_services/http-api/embedded-report.service';
import { SpinnerService } from '../../_services/spinner.service';
import { PowerBIBookmark } from '../../_objects/analytics-powerbi-bookmark';
import { AnalyticsOnlineService } from '../../_services/online/analytics-powerbi-online.service';
import { ActivatedRoute } from '@angular/router';
import { CommonSnackBarService } from '../../_services/common-snack-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { BookmarksDialogComponent } from '../bookmarks-dialog/bookmarks-dialog.component';
import { CallToActionLabel } from "../../shared/directives/cta/cta.directive";
import { IconEnum } from "@evc/web-components";
import { environment } from '../../../environments/environment';

export interface ConfigResponse {
  Id: string;
  EmbedUrl: string;
  EmbedToken: {
    Token: string;
  };
}

@Component({
    selector: 'app-analytics-powerbi-report',
    templateUrl: './analytics-powerbi-report.html',
    styleUrls: ['./analytics-powerbi-report.scss'],
    standalone: false
})
export class EmbeddedReportComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(PowerBIReportEmbedComponent) reportObj!: PowerBIReportEmbedComponent;

  existingBookmarks: PowerBIBookmark[] = [];
  sharedId: string;
  report: Report;

  callToActionLabel: CallToActionLabel = {
    label: this.translate.instant('commonHeadnavi.bookmarks'),
    icon: IconEnum.Plus
  };

  eventHandlersMap = new Map ([
    ['loaded', () => {
      this.analyticsService.getBookmarks()
        .subscribe(bookmarks => {
          this.report = this.reportObj.getReport();
          if (this.sharedId !== undefined) {
            this.existingBookmarks = bookmarks;
            const sharedBookmark = bookmarks.find(bookmark => bookmark.id === this.sharedId);

            if (sharedBookmark !== undefined) {
              this.applyBookmarktoPowerBI(sharedBookmark.bookmarkState);
            }
            else {
              this.snackBar.open(this.translate.instant("commonShareDialog.bookmarkNoLongerExists"));
            }
          }

          this.oscSpinnerService.hide();
        });
    }
    ],
    ['error', (event?: service.ICustomEvent<any>) => {
      if (event) {
        this.oscSpinnerService.hide();
      }
    }
    ]
  ]) as Map<string, (event?: service.ICustomEvent<any>, embeddedEntity?: Embed) => void | null>;

  reportConfig: IReportEmbedConfiguration = {
    type: 'report',
    embedUrl: undefined,
    tokenType: models.TokenType.Embed,
    accessToken: undefined,
    id: undefined,
    filters: [],
    settings: {
      filterPaneEnabled: true,
      navContentPaneEnabled: true,
    },
    hostname: environment.powerBiEndpointUrl
  };

  constructor(
    private embeddedReportService: EmbeddedReportService,
    private oscSpinnerService: SpinnerService,
    private analyticsService: AnalyticsOnlineService,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private snackBar: CommonSnackBarService,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    this.activatedRoute.params.subscribe(params => {
      this.sharedId = params['bookmarkId'];
    });
    this.getReport();
  }

  openAddBookmarkDialog() {
    this.dialog.open(BookmarksDialogComponent, {
      width: '800px',
      height: '512px',
      data: {
        sharedId: this.sharedId,
        report: this.report,
      }
    }).afterClosed().subscribe((selectedBookmark) => {
      if (!selectedBookmark) {
        return;
      }
      this.applyBookmarktoPowerBI(selectedBookmark.bookmarkState);
    });
  }

  getReport() {
    this.embeddedReportService.getEmbeddedReport()
      .subscribe(embed => {
        this.reportConfig = {
          ...this.reportConfig,
          id: embed.embedReport[0].reportId,
          embedUrl: embed.embedReport[0].embedUrl,
          accessToken: embed.embedToken.token
        };
      });
  }

  private applyBookmarktoPowerBI(bookmarkState: string) {
    this.reportObj.getReport().bookmarksManager.applyState(bookmarkState).then(result => {
      this.report = this.reportObj.getReport();
    }
    ,error => {
      console.error(error);
    });
  }

  ngAfterViewInit(): void {
    document.getElementsByTagName('main')[0]?.classList.add('analytics-main-background');
  }

  ngOnDestroy(): void {
    document.getElementsByTagName('main')[0].classList.remove('analytics-main-background');
  }
}
